import type { MarketId, Timeframe } from "@/stores/tradeStore";

export interface HyperliquidMarketConfig {
  coin: string;
  dex?: string; // undefined = default perp dex
  displaySymbol: string;
}

export const MARKET_CONFIG: Record<MarketId, HyperliquidMarketConfig> = {
  "BTC/USD": { coin: "BTC", displaySymbol: "BTC-USDC" },
  "ETH/USD": { coin: "ETH", displaySymbol: "ETH-USDC" },
  "SOL/USD": { coin: "SOL", displaySymbol: "SOL-USDC" },
  "ES/USD": { coin: "USA500", dex: "cash", displaySymbol: "USA500-USDT" },
  "NQ/USD": { coin: "XYZ100", dex: "xyz", displaySymbol: "XYZ100-USDC" },
  "GC/USD": { coin: "GOLD", dex: "xyz", displaySymbol: "GOLD-USDC" },
  "SI/USD": { coin: "SILVER", dex: "cash", displaySymbol: "SILVER-USDT" },
};

const HYPERLIQUID_INFO_URL = "https://api.hyperliquid.xyz/info";

// Map frontend timeframes to Hyperliquid interval strings
const timeframeToHyperliquidInterval = (tf: Timeframe): string => {
  const map: Record<Timeframe, string> = {
    "1m": "1m",
    "5m": "5m",
    "15m": "15m",
    "1h": "1h",
    "4h": "4h",
    "1d": "1d",
    "1w": "1w",
    "1M": "1M",
  };
  return map[tf];
};

export interface CandleData {
  time: number; // epoch seconds
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface HyperliquidCandle {
  t: number; // open time ms
  T: number; // close time ms
  s: string; // coin
  i: string; // interval
  o: string; // open
  c: string; // close
  h: string; // high
  l: string; // low
  v: string; // volume (base)
  n: number; // number of trades
}

/**
 * Fetch historical candles from Hyperliquid REST API.
 */
export async function fetchHyperliquidCandles(
  market: MarketId,
  timeframe: Timeframe,
  limit: number,
  signal?: AbortSignal
): Promise<CandleData[]> {
  const config = MARKET_CONFIG[market];
  const interval = timeframeToHyperliquidInterval(timeframe);

  // Build the coin identifier — dex markets need "dex:COIN" prefix for REST
  const coin = config.dex
    ? `@${config.dex === "cash" ? 8 : 9}/${config.coin}`
    : config.coin;

  // Calculate startTime: go back `limit` intervals from now
  const now = Date.now();
  const intervalMs = timeframeToMs(timeframe);
  const startTime = now - limit * intervalMs;

  const body = {
    type: "candleSnapshot",
    req: {
      coin,
      interval,
      startTime,
      endTime: now,
    },
  };

  const response = await fetch(HYPERLIQUID_INFO_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Hyperliquid API error: ${response.status} ${response.statusText}`);
  }

  const candles: HyperliquidCandle[] = await response.json();

  return candles.map((c) => ({
    time: Math.floor(c.t / 1000),
    open: parseFloat(c.o),
    high: parseFloat(c.h),
    low: parseFloat(c.l),
    close: parseFloat(c.c),
    volume: parseFloat(c.v),
  }));
}

/**
 * Fetch mid prices for all assets on a given dex (or default).
 * Returns a map of coin -> mid price string.
 */
export async function fetchHyperliquidMids(
  dex?: string
): Promise<Record<string, string>> {
  const body: Record<string, unknown> = { type: "allMids" };
  if (dex) {
    body.dex = dex;
  }

  const response = await fetch(HYPERLIQUID_INFO_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Hyperliquid mids error: ${response.status}`);
  }

  return response.json();
}

function timeframeToMs(tf: Timeframe): number {
  switch (tf) {
    case "1m":
      return 60_000;
    case "5m":
      return 300_000;
    case "15m":
      return 900_000;
    case "1h":
      return 3_600_000;
    case "4h":
      return 14_400_000;
    case "1d":
      return 86_400_000;
    case "1w":
      return 604_800_000;
    case "1M":
      return 2_592_000_000;
  }
}
